/**
 * FIELDS
 */

//completed:
const isDateSupported = () => {
    var input = document.createElement('input');
    var value = 'a';
    input.setAttribute('type', 'date');
    input.setAttribute('value', value);
    return (input.value !== value);
};

//completed:
const useOnClickOutside = (ref, handler) => {
    React.useEffect(() => {
        const listener = event => {
            // Do nothing if clicking ref's element or descendent elements
            if (!ref.current || ref.current.contains(event.target)) {
                return;
            }
            handler(event);
        };

        document.addEventListener('mousedown', listener);
        document.addEventListener('touchstart', listener);

        return () => {
            document.removeEventListener('mousedown', listener);
            document.removeEventListener('touchstart', listener);
        };
    },[ref, handler]);
};

//completed:
const deviceType = () => {
    let userAgent = navigator.userAgent || navigator.vendor || window.opera;
    if (/windows phone/i.test(userAgent)) {
        return "Windows Phone";
    }
    if (/android/i.test(userAgent)) {
        return "Android";
    }
    if (/blackberry/i.test(userAgent)) {
        return "Blackberry";
    }
    if (/opera mini/i.test(userAgent)) {
        return "Opera Mini";
    }
    // iOS detection from: http://stackoverflow.com/a/9039885/177710
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
        return "iOS";
    }
    return "Desktop";
};

//completed:
const isMobile = () => {
    return deviceType() !== 'Desktop';
};

//completed:
const groupOptions = (options, idPath, displayPath, groupPath, missingGroupingValue = 'Other') => {
    let newOptions = [];
    let ungroupedOptions = [];
    let groupedOptions = {};
    const groupingIsRequired = Boolean(_.size(groupPath));

    _.each(options, option => {
        if(groupingIsRequired){
            let groupName = Boolean(_.size(_.get(option, groupPath))) ? _.get(option, groupPath) : missingGroupingValue;
            if(_.isUndefined(groupedOptions[groupName])) groupedOptions[groupName] = [];
            groupedOptions[groupName].push({
                value: _.get(option, idPath),
                displayValue: _.get(option, displayPath),
                label: _.get(option, displayPath),
            });
        }else{
            ungroupedOptions.push({
                value: _.get(option, idPath),
                displayValue: _.get(option, displayPath),
                label: _.get(option, displayPath),
            });
        }
    });

    if(groupingIsRequired){
        const missingGroupingValueOptions = groupedOptions[missingGroupingValue];
        delete groupedOptions[missingGroupingValue];
        _.each(groupedOptions, (options, groupName) => {
            newOptions.push({
                group: groupName,
                options: options,
            });
        });
        if(_.size(missingGroupingValueOptions)){
            newOptions.push({
                group: missingGroupingValue,
                options: missingGroupingValueOptions,
            });
        }
    }else{
        newOptions.push({
            group: '_ungrouped_',
            options: ungroupedOptions,
        });
    }

    return newOptions;
};

const useAjaxLoader = (url, idPath, displayPath, groupPath, missingGroupingValue = 'Other') => {
    const [isLoading, setIsLoading] = React.useState(false);
    const [options, setOptions] = React.useState([]);
    const prevUrl = React.useRef(null);

    const fetchOptions = React.useCallback((url) => {
        setIsLoading(true);
        fetch(url)
            .then(res => res.json())
            .then((result) => {
                let parsedOptions = groupOptions(
                    result.records,
                    `${result.metaData.record}.${idPath}`,
                    `${result.metaData.record}.${displayPath}`,
                    groupPath ? `${result.metaData.record}.${groupPath}` : null,
                    missingGroupingValue
                );
                setOptions(parsedOptions);
                setIsLoading(false);
            });
    }, []);

    React.useEffect(() => {
        if(url !== prevUrl.current){
            if(url) fetchOptions(url);
            if(!url) setOptions([]);
        }
        prevUrl.current = url;
    }, [url]);

    return [isLoading, options];
};

//completed:
const TextField = React.memo(function TextField(props){
    const {
        type,
        required,
        name,
        value,
        placeholder,
        minValue,
        maxValue,
        minLength,
        maxLength,
        step,
        onChange,
    } = props;

    let textFieldRef = React.useRef(null);

    const change = React.useCallback(event => {
        onChange(name, event.target.value);
    }, [name, onChange]);

    React.useEffect(() => {
        textFieldRef.current.setCustomValidity('');
        if(minLength && _.size(value) < minLength){
            textFieldRef.current.setCustomValidity(`Min length for this field is ${minLength} characters`);
        }
        if(maxLength && _.size(value) > maxLength){
            textFieldRef.current.setCustomValidity(`Max length for this field is ${maxLength} characters`);
        }
    }, [value]);

    //useRenderDebug('TextField', {props});

    return (
        <input
            ref={textFieldRef}
            type={type}
            required={required}
            value={value || ''}
            placeholder={placeholder || ''}
            min={minValue}
            max={maxValue}
            step={step}
            onChange={change}
        />
    );
});
TextField.propTypes = {
    type: PropTypes.oneOf(['text', 'date', 'time', 'number', 'email']).isRequired,
    required: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.string,
    placeholder: PropTypes.string,
    min: PropTypes.string,
    max: PropTypes.string,
    step: PropTypes.number,
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
    onChange: PropTypes.func.isRequired,
};
TextField.defaultProps = {
    type: 'text',
    required: false,
    name: null,
    value: null,
    placeholder: null,
    min: null,
    max: null,
    step: null,
    minLength: null,
    maxLength: null,
    onChange: (name, value) => {},
};

//completed:
const TextAreaField = React.memo(function TextAreaField(props){
    const {
        required,
        name,
        value,
        placeholder,
        rows,
        minLength,
        maxLength,
        onChange,
    } = props;

    let textAreaRef = React.useRef(null);

    const change = React.useCallback(event => {
        onChange(name, event.target.value);
    }, [name, onChange]);

    React.useEffect(() => {
        textAreaRef.current.setCustomValidity('');
        if(minLength && _.size(value) < minLength){
            textAreaRef.current.setCustomValidity(`Min length for this field is ${minLength} characters`);
        }
        if(maxLength && _.size(value) > maxLength){
            textAreaRef.current.setCustomValidity(`Max length for this field is ${maxLength} characters`);
        }
    }, [value]);

    //useRenderDebug('TextAreaField', {props});

    return (
        <textarea
            ref={textAreaRef}
            required={required}
            value={value || ''}
            placeholder={placeholder || ''}
            rows={rows}
            onChange={change}
        />
    );
});
TextAreaField.propTypes = {
    required: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.string,
    placeholder: PropTypes.string,
    rows: PropTypes.number,
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
    onChange: PropTypes.func,
};
TextAreaField.defaultProps = {
    required: false,
    name: null,
    value: null,
    placeholder: null,
    rows: null,
    minLength: null,
    maxLength: null,
    onChange: (name, value) => {},
};

//completed:
const HtmlEditorField = React.memo(function HtmlEditorField(props){
    const {
        required,
        name,
        value,
        placeholder,
        rows,
        minLength,
        maxLength,
        onChange,
    } = props;
    const textAreaRef = React.useRef(null);
    const [editorInstance, setEditorInstance] = React.useState(null);

    React.useEffect(() => {
        if(editorInstance){
            editorInstance.off('keyup').on('keyup', () => {
                onChange(name, editorInstance.getContent());
            });
        }
    }, [editorInstance, onChange]);

    React.useEffect(() => {
        if(editorInstance){
            editorInstance.on('focus', () => {
                editorInstance.container.classList.add('focused');
            });
            editorInstance.on('blur', () => {
                editorInstance.container.classList.remove('focused');
            });
        }
    }, [editorInstance]);

    React.useEffect(() => {
        textAreaRef.current.setCustomValidity('');
        if(minLength && _.size(value) < minLength){
            textAreaRef.current.setCustomValidity(`Min length for this field is ${minLength} characters`);
        }
        if(maxLength && _.size(value) > maxLength){
            textAreaRef.current.setCustomValidity(`Max length for this field is ${maxLength} characters`);
        }
    }, [value, minLength, maxLength]);

    React.useEffect(() => {
        tinymce.init({
            selector: `textarea.html-editor-${_.kebabCase(name)}`,
            width: '100%',
            autoresize: true,
            min_height: 300,
            menubar: false,
            statusbar: false,
            valid_elements: 'strong/b,i/em,a[href|target=_self|style|title],span[style],p[style],div[style],code[style],blockquote[style],br[style],' +
                'ul[style],ol[style],li[style],' +
                'h1[style],h2[style],h3[style],h4[style],h5[style],h6[style],',
            relative_urls: false,
            remove_trailing_brs: true,
            //plugins: 'autoresize autolink directionality link advlist lists',
            plugins: 'autoresize autolink directionality link advlist lists paste code hr image anchor charmap',
            toolbar: 'formatselect | bold italic strikethrough forecolor backcolor | link | alignleft aligncenter alignright alignjustify  | numlist bullist | removeformat',
            toolbar_drawer: 'floating',
            autoresize_bottom_margin: 20,
            setup: editor => {
                setEditorInstance(editor);
            }
        });
    },[]);

    //useRenderDebug('HtmlEditorField', {props});

    return (
        <textarea
            ref={textAreaRef}
            className={`html-editor-${_.kebabCase(name)}`}
            required={required}
            value={value || ''}
            placeholder={placeholder || ''}
            rows={rows}
            style={{minHeight: 300}}
            readOnly
        />
    );
});
HtmlEditorField.propTypes = {
    required: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.string,
    placeholder: PropTypes.string,
    rows: PropTypes.number,
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
    onChange: PropTypes.func,
};
HtmlEditorField.defaultProps = {
    required: false,
    name: null,
    value: null,
    placeholder: null,
    rows: null,
    minLength: null,
    maxLength: null,
    onChange: (name, value) => {},
};

//completed:
const DateField = React.memo(function DateField(props){
    const {
        required,
        name,
        value,
        minValue,
        maxValue,
        onChange,
    } = props;
    const inputRef = React.useRef(null);

    const change = React.useCallback((event) => {
        if(isDateSupported()){
            onChange(name, event.target.value);
        }
    }, [name, onChange]);

    React.useEffect(() => {
        if(!isDateSupported()){
            let picker = new Pikaday({
                field: inputRef.current,
                format: 'MM/DD/YYYY',
                parse: 'YYYY-MM-DD',
                minDate: minValue ? moment(minValue).toDate() : null,
                maxDate: maxValue ? moment(maxValue).toDate() : null,
                onSelect: () => {
                    onChange(name, picker.getMoment().format('YYYY-MM-DD'));
                }
            });
        }
    }, [name, minValue, maxValue]);

    //useRenderDebug('DateField', {props});

    return (
        <input
            ref={inputRef}
            type="date"
            required={required}
            value={isDateSupported() ? value || '' : value ? moment(value).format('MM/DD/YYYY') : ''}
            placeholder={isDateSupported() ? null : 'mm/dd/yyyy'}
            min={isDateSupported() ? minValue : null}
            max={isDateSupported() ? maxValue : null}
            onChange={change}
        />
    );
});
DateField.propTypes = {
    required: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.string,
    minValue: PropTypes.string,
    maxValue: PropTypes.string,
    onChange: PropTypes.func,
};
DateField.defaultProps = {
    required: false,
    name: null,
    value: null,
    minValue: null,
    maxValue: null,
    onChange: (name, value) => {},
};

//completed:
const CheckboxField = React.memo(function CheckboxField(props){
    const {
        required,
        name,
        boxLabel,
        checked,
        isInsideCheckboxGroup,
        customValidationError,
        style,
        onChange
    } = props;
    const inputRef = React.useRef(null);

    const change = React.useCallback((event) => {
        onChange(name, !!event.target.checked);
    }, [name, onChange]);

    let HtmlTag = 'div';
    if(boxLabel) HtmlTag = 'label';

    React.useEffect(() => {
        inputRef.current.setCustomValidity(customValidationError);
    }, [customValidationError]);

    React.useEffect(() => {
        if(checked && !isInsideCheckboxGroup){
            onChange(name, true);
        }
    }, []);

    //useRenderDebug('CheckboxField', {props});

    return (
        <HtmlTag className="label" style={style || {}}>
            <input
                ref={inputRef}
                name={name}
                type="checkbox"
                required={required}
                checked={checked}
                title={customValidationError ? customValidationError : null}
                onChange={change}
            />
            {boxLabel && <span dangerouslySetInnerHTML={{__html: boxLabel}}/>}
        </HtmlTag>
    );
});
CheckboxField.propTypes = {
    required: PropTypes.bool,
    name: PropTypes.string,
    boxLabel: PropTypes.string,
    checked: PropTypes.bool,
    customValidationError: PropTypes.string,
    style: PropTypes.object,
    isInsideCheckboxGroup: PropTypes.bool,
    onChange: PropTypes.func
};
CheckboxField.defaultProps = {
    required: false,
    name: null,
    boxLabel: null,
    checked: false,
    isInsideCheckboxGroup: false,
    customValidationError: "",
    onChange: (name, value) => {},
};

//completed:
const CheckboxGroupField = React.memo(function CheckboxGroupField(props){
    const {
        required,
        multiMin,
        multiMax,
        name,
        value,
        options,
        autoFlex,
        columns,
        datagroupAlias,
        enableCollapsibleGroups,
        onChange,
    } = props;
    const rootRef = React.useRef(null);
    const [isRequired, setIsRequired] = React.useState(required);
    const [minMaxError, setMinMaxError] = React.useState(null);
    const [expanded, setExpanded] = React.useState([]);

    const toggleGroup = React.useCallback(groupName => event => {
        setExpanded(current => {
            let expanded = _.clone(current);
            if(_.includes(expanded, groupName)){
                _.remove(expanded, val => val === groupName);
            }else{
                expanded.push(groupName);
            }
            return expanded;
        });
    }, []);

    const change = React.useCallback(valueId => (cbName, checked) => {
        let values = _.clone(value);
        if(checked && !_.includes(values, valueId)) {
            values.push(valueId);
        }else{
            _.remove(values, (value) => {
                return value === valueId;
            });
        }
        onChange(name, values);
    }, [name, value]);

    React.useEffect(() => {
        rootRef.current.dispatchEvent(new Event('parentchange'));
    }, [value]);

    React.useEffect(() => {
        if (required && !_.size(value)) {
            setIsRequired(true);
        } else if (parseInt(multiMin) && _.size(value) > 0 && _.size(value) < parseInt(multiMin)) {
            setMinMaxError(`You need to select at least ${multiMin} options`);
        } else if (parseInt(multiMax) && _.size(value) > 0 && _.size(value) > parseInt(multiMax)) {
            setMinMaxError(`You need to select maximum ${multiMax} options`);
        } else {
            setIsRequired(false);
            setMinMaxError('');
        }
    }, [required, value, multiMin, multiMax]);

    let checkboxes = [];
    _.each(options, (group, index) => {
        let groupName = group.group;

        let groupCollapseIcon = null;
        if(enableCollapsibleGroups && _.includes(expanded, groupName)){
            groupCollapseIcon = <i className={'fa fa-chevron-up collapse-icon'} aria-hidden={'true'}/>;
        }
        if(enableCollapsibleGroups && !_.includes(expanded, groupName)){
            groupCollapseIcon = <i className={'fa fa-chevron-down collapse-icon'} aria-hidden={'true'}/>;
        }

        let groupOptions = [];
        let groupItemCount = 0;
        _.each(group.options, (option, index) => {
            groupOptions.push(
                <CheckboxField
                    key={`radio-${index}`}
                    name={`${name}-${index}`}
                    required={isRequired}
                    customValidationError={minMaxError}
                    checked={_.indexOf(value, option.value) > -1}
                    isInsideCheckboxGroup={true}
                    boxLabel={option.label}
                    onChange={change(option.value)}
                />
            );
            groupItemCount++;
            if(!autoFlex && groupItemCount === columns){
                groupItemCount = 0;
                groupOptions.push(
                    <div key={`break-${index}`} className="break"/>
                );
            }
        });
        checkboxes.push(
            <div className={'checkbox-radio-group-section'} key={`group-${groupName}`}>
                {Boolean(groupName !== '_ungrouped_') &&
                <div className={`checkbox-radio-group-title ${enableCollapsibleGroups ? 'clickable' : ''}`} onClick={toggleGroup(groupName)}>
                    {groupCollapseIcon}
                    {groupName}
                </div>
                }
                <div className={`checkbox-radio-group-options ${enableCollapsibleGroups && !_.includes(expanded, groupName) ? 'collapsed' : ''}`}>
                    {groupOptions}
                </div>
            </div>
        );
    });

    //useRenderDebug('CheckboxGroupField', {props});

    return (
        <div
            ref={rootRef}
            className={`checkbox-radio-group ${autoFlex ? 'stack-auto' : 'stack-left'} columns-${columns}`}
            data-value={value}
            data-datagroup-alias={datagroupAlias}
        >
            {checkboxes}
        </div>
    );
});
CheckboxGroupField.propTypes = {
    required: PropTypes.bool,
    multiMin: PropTypes.number,
    multiMax: PropTypes.number,
    name: PropTypes.string.isRequired,
    value: PropTypes.array,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.bool,
            ]),
            label: PropTypes.string,
        })
    ),
    autoFlex: PropTypes.bool,
    columns: PropTypes.number,
    datagroupAlias: PropTypes.string,
    enableCollapsibleGroups: PropTypes.bool,
    onChange: PropTypes.func,
};
CheckboxGroupField.defaultProps = {
    required: false,
    multiMin: null,
    multiMax: null,
    name: null,
    value: [],
    options: [],
    autoFlex: false,
    columns: 0,
    datagroupAlias: null,
    enableCollapsibleGroups: false,
    onChange: (name, value) => {},
};

//completed:
const RadioField = React.memo(function RadioField(props){
    const {
        required,
        name,
        boxLabel,
        checked,
        value,
        style,
        onChange,
    } = props;

    const change = React.useCallback(event => {
        onChange(name, value);
    }, [name, value, onChange]);

    //useRenderDebug('RadioField', {props});

    return (
        <label className={'label'} style={style || {}}>
            <input
                type={'radio'}
                name={name}
                required={required}
                checked={checked}
                value={value}
                onChange={change}
            />
            {boxLabel && <span dangerouslySetInnerHTML={{__html: boxLabel}}/>}
        </label>
    );
});
RadioField.propTypes = {
    required: PropTypes.bool,
    name: PropTypes.string.isRequired,
    boxLabel: PropTypes.string,
    checked: PropTypes.bool,
    value: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
        PropTypes.bool,
    ]),
    style: PropTypes.object,
    onChange: PropTypes.func,
};
RadioField.defaultProps = {
    required: false,
    name: null,
    boxLabel: null,
    checked: false,
    value: null,
    onChange: (name, value) => {},
};

//completed:
const RadioGroupField = React.memo(function RadioGroupField(props){
    const {
        required,
        name,
        value,
        options,
        autoFlex,
        columns,
        datagroupAlias,
        enableCollapsibleGroups,
        onChange,
    } = props;
    const rootRef = React.useRef(null);
    const [expanded, setExpanded] = React.useState([]);

    const toggleGroup = React.useCallback(groupName => event => {
        setExpanded(current => {
            let expanded = _.clone(current);
            if(_.includes(expanded, groupName)){
                _.remove(expanded, val => val === groupName);
            }else{
                expanded.push(groupName);
            }
            return expanded;
        });
    }, []);

    React.useEffect(() => {
        rootRef.current.dispatchEvent(new Event('parentchange'));
    }, [value]);

    let radios = [];
    _.each(options, (group, index) => {
        let groupName = group.group;

        let groupCollapseIcon = null;
        if(enableCollapsibleGroups && _.includes(expanded, groupName)){
            groupCollapseIcon = <i className={'fa fa-chevron-up collapse-icon'} aria-hidden={'true'}/>;
        }
        if(enableCollapsibleGroups && !_.includes(expanded, groupName)){
            groupCollapseIcon = <i className={'fa fa-chevron-down collapse-icon'} aria-hidden={'true'}/>;
        }

        let groupOptions = [];
        let groupItemCount = 0;
        _.each(group.options, (option, index) => {
            groupOptions.push(
                <RadioField
                    key={`radio-${index}`}
                    required={required}
                    name={name}
                    checked={value === option.value}
                    value={option.value}
                    boxLabel={option.label}
                    onChange={onChange}
                />
            );
            groupItemCount++;
            if(!autoFlex && groupItemCount === columns){
                groupItemCount = 0;
                groupOptions.push(
                    <div key={`break-${index}`} className="break"/>
                );
            }
        });
        radios.push(
            <div className={'checkbox-radio-group-section'} key={`group-${groupName}`}>
                {Boolean(groupName !== '_ungrouped_') &&
                <div className={`checkbox-radio-group-title ${enableCollapsibleGroups ? 'clickable' : ''}`} onClick={toggleGroup(groupName)}>
                    {groupCollapseIcon}
                    {groupName}
                </div>
                }
                <div className={`checkbox-radio-group-options ${enableCollapsibleGroups && !_.includes(expanded, groupName) ? 'collapsed' : ''}`}>
                    {groupOptions}
                </div>
            </div>
        );
    });

    //useRenderDebug('RadioGroupField', {props});

    return (
        <div
            ref={rootRef}
            className={`checkbox-radio-group ${autoFlex ? 'stack-auto' : 'stack-left'} columns-${columns}`}
            data-value={value}
            data-datagroup-alias={datagroupAlias}
        >
            {radios}
        </div>
    );
});
RadioGroupField.propTypes = {
    required: PropTypes.bool,
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
        PropTypes.bool,
    ]),
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.oneOfType([
                PropTypes.number,
                PropTypes.string,
                PropTypes.bool,
            ]),
            label: PropTypes.string,
        })
    ),
    autoFlex: PropTypes.bool,
    columns: PropTypes.number,
    datagroupAlias: PropTypes.string,
    enableCollapsibleGroups: PropTypes.bool,
    onChange: PropTypes.func,
};
RadioGroupField.defaultProps = {
    required: false,
    name: null,
    value: null,
    options: [],
    autoFlex: false,
    columns: 0,
    datagroupAlias: null,
    enableCollapsibleGroups: false,
    onChange: (name, value) => {},
};

//completed:
const SelectField = React.memo(function SelectField(props){
    const {
        isLoading,
        required,
        name,
        value,
        placeholder,
        emptyListText,
        showEmptyOption,
        options,
        datagroupAlias,
        onChange,
    } = props;
    const rootRef = React.useRef(null);

    const change = React.useCallback(event => {
        onChange(name, event.target.value);
    }, [name, onChange]);

    React.useEffect(() => {
        setTimeout(() => {
            if(rootRef.current) rootRef.current.dispatchEvent(new Event('parentchange'));
        }, 300);
    }, [value]);

    React.useEffect(() => {
        let allOptions = [];
        _.each(options, (options) => {
            allOptions = _.concat(allOptions, options.options);
        });
        if(_.size(allOptions) && !_.includes(_.map(allOptions, 'value'), value)){
            onChange(name, '');
        }
    }, [value, options]);

    let selectOptions = [];
    if(isLoading){
        selectOptions.push(
            <option key="loading" disabled value="" style={{display: 'none'}}>Loading...</option>
        );
    }
    if(placeholder) {
        selectOptions.push(
            <option key="placeholder" disabled value="" style={{display: 'none'}}>{placeholder}</option>
        );
    }

    if(showEmptyOption && _.size(options)){
        selectOptions.push(
            <option key="blank" value=""/>
        );
    }

    if(emptyListText && !_.size(options)){
        selectOptions.push(
            <option key="empty-text" disabled value="">{emptyListText}</option>
        );
    }

    _.each(options, (group, index) => {
        let groupName = group.group;
        if(groupName === '_ungrouped_'){
            _.each(group.options, (option, index) => {
                selectOptions.push(
                    <option key={`option-${index}`} disabled={option.disabled || false} value={option.value}>{option.displayValue}</option>
                );
            });
        }else{
            let groupOptions = [];
            _.each(group.options, (option, index) => {
                groupOptions.push(
                    <option key={`option-${groupName}-${index}`} disabled={option.disabled || false} value={option.value}>{option.displayValue}</option>
                );
            });
            selectOptions.push(
                <optgroup key={`group-${groupName}`} label={groupName} style={{fontWeight: 'bold'}}>
                    {groupOptions}
                </optgroup>
            );
        }
    });

    //useRenderDebug('SelectField', {props});

    return (
        <select
            ref={rootRef}
            disabled={isLoading}
            required={required}
            value={value || ''}
            placeholder={placeholder || ''}
            onChange={change}
            data-value={value}
            data-datagroup-alias={datagroupAlias}
        >
            {selectOptions}
        </select>
    );
});
SelectField.propTypes = {
    isLoading: PropTypes.bool,
    required: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
        PropTypes.bool,
        PropTypes.array,
    ]),
    placeholder: PropTypes.string,
    emptyListText: PropTypes.string,
    datagroupAlias: PropTypes.string,
    showEmptyOption: PropTypes.bool,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.oneOfType([
                PropTypes.number,
                PropTypes.string,
                PropTypes.bool,
            ]),
            displayValue: PropTypes.string,
            disabled: PropTypes.bool,
            group: PropTypes.string,
        })
    ),
    onChange: PropTypes.func.isRequired,
};
SelectField.defaultProps = {
    isLoading: false,
    required: false,
    name: null,
    value: null,
    placeholder: null,
    emptyListText: 'No options available',
    showEmptyOption: true,
    options: [],
    datagroupAlias: null,
    onChange: (name, value) => {},
};

//completed:
const MultiSelectField = React.memo(function MultiSelectField(props){
    const {
        isLoading,
        required,
        name,
        multiMin,
        multiMax,
        value,
        placeholder,
        emptyListText,
        options: optionsFromProps,
        datagroupAlias,
        onChange,
    } = props;

    let multiselectRef = React.useRef(null);
    let selectRef = React.useRef(null);
    let inputRef = React.useRef(null);

    const [options, setOptions] = React.useState(optionsFromProps);
    const [visibleOptions, setVisibleOptions] = React.useState(optionsFromProps);
    const [optionListIsOpen, setOptionListIsOpen] = React.useState(false);
    const [hoveredOptionValue, setHoveredOptionValue] = React.useState(null);
    const [validationError, setValidationError] = React.useState("");
    const [allOptions, setAllOptions] = React.useState([]);
    const [allVisibleOptions, setAllVisibleOptions] = React.useState([]);

    const handleChange = React.useCallback((event) => {
        let values = [];
        let options = event.target.options;
        for (let i = 0, length = options.length; i < length; i++) {
            if (options[i].selected) {
                values.push(options[i].value);
            }
        }
        onChange(name, values);
    }, [onChange, name]);

    const handleCustomChange = React.useCallback(selectedValue => event => {
        let values = _.clone(value);
        if(_.indexOf(values, selectedValue) < 0){
            values.push(selectedValue);
        }else{
            _.remove(values, (value) => value === selectedValue);
        }
        onChange(name, values);
    }, [onChange, value, name]);

    const handleRemove = React.useCallback(valueToRemove => event => {
        let values = _.clone(value);
        _.remove(values, (value) => value === valueToRemove);
        onChange(name, values);
        event.stopPropagation();
        event.preventDefault();
        return false;
    }, [onChange, value, name]);

    const hoverOption = React.useCallback((direction) => {
        setHoveredOptionValue(hoveredOption => {
            let nextIndex = null;
            if(!hoveredOption){
                if(direction === 'next') nextIndex = 0;
                if(direction === 'prev') nextIndex = _.size(allVisibleOptions) - 1;
            }else{
                if(direction === 'next'){
                    nextIndex = _.findIndex(allVisibleOptions, {value: hoveredOption}) + 1;
                    if(nextIndex === _.size(allVisibleOptions)) nextIndex = 0;
                }
                if(direction === 'prev'){
                    nextIndex = _.findIndex(allVisibleOptions, {value: hoveredOption}) - 1;
                    if(nextIndex < 0) nextIndex = _.size(allVisibleOptions) - 1;
                }
            }
            return allVisibleOptions[nextIndex].value
        });
    }, [allVisibleOptions]);

    const onInputClick = React.useCallback((event) => {
        setOptionListIsOpen(optionListIsOpen => !optionListIsOpen);
    }, []);

    const onInputKeyDown = React.useCallback((event) => {
        switch(event.which) {
            case 27: //Tab
                if(optionListIsOpen) setOptionListIsOpen(false);
                return;
            case 9: //Esc
                if(optionListIsOpen) setOptionListIsOpen(false);
                return;
            case 38: //Up
                if(!optionListIsOpen) setOptionListIsOpen(true);
                if(optionListIsOpen) hoverOption('prev');
                event.preventDefault();
                return;
            case 40: //Down
                if(!optionListIsOpen) setOptionListIsOpen(true);
                if(optionListIsOpen) hoverOption('next');
                event.preventDefault();
                return;
            case 32: //Space
                if(optionListIsOpen && hoveredOptionValue) {
                    handleCustomChange(hoveredOptionValue)();
                    event.preventDefault();
                }
                return;
            case 13: //Enter
                if(optionListIsOpen && hoveredOptionValue) {
                    handleCustomChange(hoveredOptionValue)();
                    event.preventDefault();
                }
                return;
            default:
                return;
        }
    }, [optionListIsOpen, hoveredOptionValue, hoverOption]);

    const onSearchInputKeyDown = React.useCallback(event => {
        switch(event.which) {
            case 27: //Esc
                if(optionListIsOpen) setOptionListIsOpen(false);
                return;
            case 9: //Tab
                if(optionListIsOpen) {
                    inputRef.current.focus();
                    hoverOption('next');
                }
                event.preventDefault();
                return;
            default:
                return;
        }
    }, [optionListIsOpen]);

    const onSearchInputKeyUp = React.useCallback(event => {
        let keyword = event.target.value.toLowerCase();
        if(Boolean(keyword.length)){
            let filteredOptionsObject = {};
            _.each(options, ({group: groupName, options: groupOptions}) => {
                _.each(groupOptions, option => {
                    if(_.includes(groupName.toLowerCase(), keyword) || _.includes(option.displayValue.toLowerCase(), keyword)){
                        if(!filteredOptionsObject[groupName]){
                            filteredOptionsObject[groupName] = [];
                        }
                        filteredOptionsObject[groupName].push(option);
                    }
                });
            });

            let filteredOptions = [];
            _.each(filteredOptionsObject, (groupOptions, groupName) => {
                filteredOptions.push({
                    group: groupName,
                    options: groupOptions,
                });
            });

            setVisibleOptions(filteredOptions);
        }else{
            setVisibleOptions(options);
        }
    }, [optionListIsOpen, options]);

    const onOptionHover = React.useCallback(optionValue => event => {
        setHoveredOptionValue(optionValue);
    }, []);

    useOnClickOutside(multiselectRef, () => setOptionListIsOpen(false));

    React.useEffect(() => {
        setOptions(optionsFromProps);
        setVisibleOptions(optionsFromProps);
    }, [optionsFromProps]);

    React.useEffect(() => {
        selectRef.current.dispatchEvent(new Event('parentchange'));
    }, [value]);

    React.useEffect(() => {
        let validationError = '';
        if (required && !_.size(value)) {
            validationError = `Please select an item in the list`;
        } else if (multiMin && _.size(value) && _.size(value) < multiMin) {
            validationError = `You need to select at least ${multiMin} options`;
        } else if (multiMax && _.size(value) && _.size(value) > multiMax) {
            validationError = `You need to select maximum ${multiMax} options`;
        }

        setValidationError(validationError);
        selectRef.current.setCustomValidity(validationError);
    }, [required, value, multiMin, multiMax]);

    React.useEffect(() => {
        let tmp = [];
        _.each(options, (options) => {
            tmp = _.concat(tmp, options.options);
        });
        setAllOptions(tmp);
    }, [options]);

    React.useEffect(() => {
        let tmp = [];
        _.each(visibleOptions, (options) => {
            tmp = _.concat(tmp, options.options);
        });
        setAllVisibleOptions(tmp);
    }, [visibleOptions]);

    let placeholderText = null;
    let nativeOptions = [];
    let customOptions = [];
    if(placeholder) {
        placeholderText = placeholder;
    }
    if(isLoading){
        placeholderText = 'Loading...';
    }
    if(_.size(value)){
        placeholderText = null;
    }

    if(emptyListText && !_.size(allOptions)){
        nativeOptions.push(
            <option key="empty-text" disabled value="">{emptyListText}</option>
        );
    }

    _.each(visibleOptions, (group, index) => {
        let groupName = group.group;
        if(groupName === '_ungrouped_'){
            _.each(group.options, (option, index) => {
                nativeOptions.push(
                    <option key={`option-${index}`} disabled={option.disabled || false} value={option.value}>{option.displayValue}</option>
                );
                customOptions.push(
                    <div
                        className={`option ${_.indexOf(value, option.value) > -1 ? 'checked' : ''} ${hoveredOptionValue === option.value ? 'hovered' : ''}`}
                        key={`option-${index}`}
                        onClick={handleCustomChange(option.value)}
                        onMouseEnter={onOptionHover(option.value)}
                        onMouseLeave={onOptionHover(null)}
                    >
                        <span className="checkbox"/>
                        <span className="text">{option.displayValue}</span>
                    </div>
                );
            });
        }else{
            let groupNativeOptions = [];
            let groupCustomOptions = [];
            _.each(group.options, (option, index) => {
                groupNativeOptions.push(
                    <option key={`option-${groupName}-${index}`} disabled={option.disabled || false} value={option.value}>{option.displayValue}</option>
                );
                groupCustomOptions.push(
                    <div
                        className={`option ${_.indexOf(value, option.value) > -1 ? 'checked' : ''} ${hoveredOptionValue === option.value ? 'hovered' : ''}`}
                        key={`option-${groupName}-${index}`}
                        onClick={handleCustomChange(option.value)}
                        onMouseEnter={onOptionHover(option.value)}
                        onMouseLeave={onOptionHover(null)}
                    >
                        <span className="checkbox"/>
                        <span className="text">{option.displayValue}</span>
                    </div>
                );
            });
            nativeOptions.push(
                <optgroup key={`group-${groupName}`} label={groupName} style={{fontWeight: 'bold'}}>
                    {groupNativeOptions}
                </optgroup>
            );
            customOptions.push(
                <div className="option-group" key={`group-${groupName}`}>
                    <div className="option-group-title">{groupName}</div>
                    {groupCustomOptions}
                </div>
            );
        }
    });

    let optionTags = [];
    _.each(value, (val) => {
        let selectedTag = _.find(allOptions, option => {return option.value === val});
        if(selectedTag) {
            optionTags.push(
                <div key={`selected-option-value-${val}`} className="option">
                    <span className="text">{selectedTag.displayValue}</span>
                    <span className="remove" onClick={handleRemove(val)}/>
                </div>
            );
        }
    });

    let showSearchField = _.size(allOptions) > 20;

    //useRenderDebug('MultiSelectField', {props, optionListIsOpen, hoveredOptionValue, validationError, handleChange, handleCustomChange, handleRemove, hoverOption, onInputClick, onInputKeyDown, onOptionHover});

    return (
        <div className={['multiselect', (_.size(validationError) ? 'invalid' : '')].join(' ')} ref={multiselectRef}>
            <select
                ref={selectRef}
                className={isMobile() ? 'selectable' : ''}
                tabIndex="-1"
                multiple={true}
                disabled={isLoading}
                required={required}
                value={value || ''}
                data-value={value}
                data-datagroup-alias={datagroupAlias}
                onChange={handleChange}
            >
                {nativeOptions}
            </select>
            <input
                ref={inputRef}
                type="text"
                placeholder={placeholderText}
                disabled={isLoading}
                readOnly={true}
                onKeyDown={onInputKeyDown}
                onClick={onInputClick}
                title={validationError.length > 0 ? validationError : null}
            />
            <div className="selected-options">{optionTags}</div>
            {!isMobile() &&
                <div className={`option-list ${optionListIsOpen ? 'open' : ''}`}>
                    {showSearchField &&
                        <div className={'option-search'}>
                            <input type="text" onKeyDown={onSearchInputKeyDown} onKeyUp={onSearchInputKeyUp}/>
                        </div>
                    }
                    {customOptions}
                </div>
            }
        </div>
    );
});
MultiSelectField.propTypes = {
    isLoading: PropTypes.bool,
    required: PropTypes.bool,
    name: PropTypes.string,
    multiMin: PropTypes.number,
    multiMax: PropTypes.number,
    value: PropTypes.array,
    placeholder: PropTypes.string,
    emptyListText: PropTypes.string,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string,
            displayValue: PropTypes.string,
            disabled: PropTypes.bool,
            group: PropTypes.string,
        })
    ),
    datagroupAlias: PropTypes.string,
    onChange: PropTypes.func.isRequired,
};
MultiSelectField.defaultProps = {
    isLoading: false,
    required: false,
    name: null,
    multiMin: null,
    multiMax: null,
    value: [],
    placeholder: null,
    emptyListText: 'No options available',
    options: [],
    datagroupAlias: null,
    onChange: (name, value) => {},
};

//=======================================================================
// FIELD TYPES ==========================================================
//=======================================================================

//completed:
const TaskFormFieldTypeText = React.memo(function TaskFormFieldTypeText(props){
    const {
        required,
        value,
        name,
        placeholder,
        minLength,
        maxLength,
        onChange,
    } = props;

    //useRenderDebug('TaskFormFieldTypeText', {props});

    return (
        <TextField
            type={'text'}
            required={required}
            name={name}
            value={value}
            placeholder={placeholder}
            minLength={minLength}
            maxLength={maxLength}
            onChange={onChange}
        />
    );
});
TaskFormFieldTypeText.propTypes = {
    required: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.string,
    placeholder: PropTypes.string,
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
    onChange: PropTypes.func,
};
TaskFormFieldTypeText.defaultProps = {
    required: false,
    name: '',
    value: '',
    placeholder: null,
    minLength: null,
    maxLength: null,
    onChange: (name, value) => {},
};
TaskFormFieldTypeText.displayName = 'FieldType: Text';

//completed:
const TaskFormFieldTypeCheckbox = React.memo(function TaskFormFieldTypeCheckbox(props){
    const {
        required,
        name,
        checked,
        boxLabel,
        onChange,
    } = props;

    //useRenderDebug('TaskFormFieldTypeCheckbox', {props});

    return (
        <CheckboxField
            required={required}
            name={name}
            checked={checked}
            boxLabel={boxLabel}
            onChange={onChange}
        />
    );
});
TaskFormFieldTypeCheckbox.propTypes = {
    name: PropTypes.string,
    required: PropTypes.bool,
    checked: PropTypes.bool,
    boxLabel: PropTypes.string,
    onChange: PropTypes.func,
};
TaskFormFieldTypeCheckbox.defaultProps = {
    name: '',
    required: false,
    checked: false,
    boxLabel: null,
    onChange: (name, value) => {},
};
TaskFormFieldTypeCheckbox.displayName = 'FieldType: Checkbox';

//completed:
const TaskFormFieldTypeBoolean = React.memo(function TaskFormFieldTypeBoolean(props){
    const {
        required,
        name,
        value,
        onChange,
        yesLabel,
        noLabel,
        indexFromParent,
    } = props;

    const options = React.useRef([{
        group: '_ungrouped_',
        options: [{
            value: true,
            label: yesLabel,
        }, {
            value: false,
            label: noLabel,
        }]
    }]);

    //useRenderDebug('TaskFormFieldTypeBoolean', {props});

    return (
        <RadioGroupField
            required={required}
            name={indexFromParent ? `${name}-${indexFromParent}` : name}
            value={value}
            onChange={onChange}
            options={options.current}
        />
    );
});
TaskFormFieldTypeBoolean.propTypes = {
    required: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.bool,
    yesLabel: PropTypes.string,
    noLabel: PropTypes.string,
    indexFromParent: PropTypes.number,
    onChange: PropTypes.func,
};
TaskFormFieldTypeBoolean.defaultProps = {
    required: false,
    name: '',
    value: null,
    yesLabel: 'Yes',
    noLabel: 'No',
    indexFromParent: null,
    onChange: (name, value) => {},
};
TaskFormFieldTypeBoolean.displayName = 'FieldType: Boolean';

//completed:
const TaskFormFieldTypeYesNo = React.memo(function TaskFormFieldTypeYesNo(props){
    const {
        required,
        name,
        value,
        yesLabel,
        noLabel,
        showYesExplanation,
        showNoExplanation,
        yesExplanationLabel,
        noExplanationLabel,
        yesExplanationRequired,
        noExplanationRequired,
        yesMaxChars,
        noMaxChars,
        indexFromParent,
        onChange,
    } = props;

    const onOptionChange = React.useCallback((n, v) => {
        let newValue = _.clone(value);
        _.set(newValue, 'option', v);
        _.set(newValue, 'explanation', value.option === v ? value.explanation : '');
        onChange(name, newValue);
    }, [name, value]);

    const onExplanationChange = React.useCallback((n, v) => {
        let newValue = _.clone(value);
        _.set(newValue, 'explanation', v);
        onChange(name, newValue);
    }, [name, value]);

    let explanationField = null;
    if(value.option !== null && (value.option === 1 && showYesExplanation || value.option === 0 && showNoExplanation)){
        explanationField = (
            <TextAreaField
                required={value.option === 1 ? yesExplanationRequired : value.option === 0 ? noExplanationRequired : null}
                name={`${name}_explanation`}
                value={value.explanation}
                placeholder={value.option === 1 ? yesExplanationLabel : noExplanationLabel}
                rows={4}
                maxLength={value.option === 1 ? yesMaxChars : value.option === 0 ? noMaxChars : null}
                onChange={onExplanationChange}
            />
        );
    }

    const options = React.useRef({
        group: '_ungrouped_',
        options: [{
            value: 1,
            label: yesLabel,
        }, {
            value: 0,
            label: noLabel,
        }]
    });

    //useRenderDebug('TaskFormFieldTypeYesNo', {props});

    return (
        <div className="yes-no-field">
            <RadioGroupField
                required={required}
                name={indexFromParent ? `${name}-${indexFromParent}-option` : `${name}-option`}
                value={value.option}
                onChange={onOptionChange}
                options={options.current}
            />
            {explanationField}
        </div>
    );
});
TaskFormFieldTypeYesNo.propTypes = {
    required: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.object,
    yesLabel: PropTypes.string,
    noLabel: PropTypes.string,
    showYesExplanation: PropTypes.bool,
    showNoExplanation: PropTypes.bool,
    yesExplanationLabel: PropTypes.string,
    noExplanationLabel: PropTypes.string,
    yesExplanationRequired: PropTypes.bool,
    noExplanationRequired: PropTypes.bool,
    yesMaxChars: PropTypes.number,
    noMaxChars: PropTypes.number,
    indexFromParent: PropTypes.number,
    onChange: PropTypes.func,
};
TaskFormFieldTypeYesNo.defaultProps = {
    required: false,
    name: '',
    value: {
        option: null,
        explanation: ''
    },
    yesLabel: 'Yes',
    noLabel: 'No',
    showYesExplanation: false,
    showNoExplanation: false,
    yesExplanationLabel: 'Explain',
    noExplanationLabel: 'Explain',
    yesExplanationRequired: false,
    noExplanationRequired: false,
    yesMaxChars: null,
    noMaxChars: null,
    indexFromParent: null,
    onChange: (name, value) => {},
};
TaskFormFieldTypeYesNo.displayName = 'FieldType: YesNo';

//completed:
const TaskFormFieldTypeDate = React.memo(function TaskFormFieldTypeDate(props){
    const {
        required,
        name,
        value,
        placeholder,
        minValue,
        maxValue,
        onChange,
    } = props;

    //useRenderDebug('TaskFormFieldTypeDate', {props});

    return (
        <DateField
            required={required}
            name={name}
            placeholder={placeholder}
            value={value}
            minValue={minValue}
            maxValue={maxValue}
            onChange={onChange}
        />
    );
});
TaskFormFieldTypeDate.propTypes = {
    required: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.string,
    placeholder: PropTypes.string,
    minValue: PropTypes.string,
    maxValue: PropTypes.string,
    onChange: PropTypes.func,
};
TaskFormFieldTypeDate.defaultProps = {
    required: false,
    name: '',
    value: '',
    placeholder: null,
    minValue: null,
    maxValue: null,
    onChange: (name, value) => {},
};
TaskFormFieldTypeDate.displayName = 'FieldType: Date';

//completed:
const TaskFormFieldTypeTime = React.memo(function TaskFormFieldTypeTime(props){
    const {
        required,
        value,
        name,
        placeholder,
        minValue,
        maxValue,
        onChange,
    } = props;

    //useRenderDebug('TaskFormFieldTypeTime', {props});

    return (
        <TextField
            type={'time'}
            required={required}
            name={name}
            value={value}
            placeholder={placeholder}
            minValue={minValue}
            maxValue={maxValue}
            onChange={onChange}
        />
    );
});
TaskFormFieldTypeTime.propTypes = {
    required: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.string,
    placeholder: PropTypes.string,
    minValue: PropTypes.string,
    maxValue: PropTypes.string,
    onChange: PropTypes.func,
};
TaskFormFieldTypeTime.defaultProps = {
    required: false,
    name: '',
    value: '',
    placeholder: null,
    minValue: null,
    maxValue: null,
    onChange: (name, value) => {},
};
TaskFormFieldTypeTime.displayName = 'FieldType: Time';

//completed:
const TaskFormFieldTypeDecimal = React.memo(function TaskFormFieldTypeDecimal(props){
    const {
        required,
        value,
        name,
        placeholder,
        minValue,
        maxValue,
        step,
        onChange,
    } = props;

    //useRenderDebug('TaskFormFieldTypeDecimal', {props});

    return (
        <TextField
            type={'number'}
            required={required}
            name={name}
            value={value}
            placeholder={placeholder}
            minValue={minValue}
            maxValue={maxValue}
            step={step}
            onChange={onChange}
        />
    );
});
TaskFormFieldTypeDecimal.propTypes = {
    required: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.string,
    placeholder: PropTypes.string,
    minValue: PropTypes.number,
    maxValue: PropTypes.number,
    step: PropTypes.number,
    onChange: PropTypes.func,
};
TaskFormFieldTypeDecimal.defaultProps = {
    required: false,
    name: '',
    value: '',
    placeholder: null,
    minValue: null,
    maxValue: null,
    step: 0.1,
    onChange: (name, value) => {},
};
TaskFormFieldTypeDecimal.displayName = 'FieldType: Decimal';

//completed:
const TaskFormFieldTypeNumber = React.memo(function TaskFormFieldTypeNumber(props){
    const {
        required,
        value,
        name,
        placeholder,
        minValue,
        maxValue,
        step,
        onChange,
    } = props;

    //useRenderDebug('TaskFormFieldTypeNumber', {props});

    return (
        <TextField
            type={'number'}
            required={required}
            name={name}
            value={value}
            placeholder={placeholder}
            minValue={minValue}
            maxValue={maxValue}
            step={step}
            onChange={onChange}
        />
    );
});
TaskFormFieldTypeNumber.propTypes = {
    required: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.string,
    placeholder: PropTypes.string,
    minValue: PropTypes.number,
    maxValue: PropTypes.number,
    step: PropTypes.number,
    onChange: PropTypes.func,
};
TaskFormFieldTypeNumber.defaultProps = {
    required: false,
    name: '',
    value: '',
    placeholder: null,
    minValue: null,
    maxValue: null,
    step: 1,
    onChange: (name, value) => {},
};
TaskFormFieldTypeNumber.displayName = 'FieldType: Number';

//completed:
const TaskFormFieldTypeTextArea = React.memo(function TaskFormFieldTypeTextArea(props){
    const {
        required,
        value,
        name,
        placeholder,
        minLength,
        maxLength,
        rows,
        onChange,
    } = props;

    //useRenderDebug('TaskFormFieldTypeTextArea', {props});

    return (
        <TextAreaField
            type={'text'}
            required={required}
            name={name}
            value={value}
            placeholder={placeholder}
            minLength={minLength}
            maxLength={maxLength}
            rows={rows}
            onChange={onChange}
        />
    );
});
TaskFormFieldTypeTextArea.propTypes = {
    required: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.string,
    placeholder: PropTypes.string,
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
    rows: PropTypes.number,
    onChange: PropTypes.func,
};
TaskFormFieldTypeTextArea.defaultProps = {
    required: false,
    name: '',
    value: '',
    placeholder: null,
    minLength: null,
    maxLength: null,
    rows: 4,
    onChange: (name, value) => {},
};
TaskFormFieldTypeTextArea.displayName = 'FieldType: TextArea';

//completed:
const TaskFormFieldTypeHtml = React.memo(function TaskFormFieldTypeHtml(props){
    const {
        required,
        value,
        name,
        placeholder,
        maxLength,
        rows,
        onChange,
    } = props;

    //useRenderDebug('TaskFormFieldTypeHtml', {props});

    return (
        <HtmlEditorField
            required={required}
            name={name}
            value={value}
            placeholder={placeholder}
            maxLength={maxLength}
            rows={rows}
            onChange={onChange}
        />
    );
});
TaskFormFieldTypeHtml.propTypes = {
    required: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.string,
    placeholder: PropTypes.string,
    maxLength: PropTypes.number,
    rows: PropTypes.number,
    onChange: PropTypes.func,
};
TaskFormFieldTypeHtml.defaultProps = {
    required: false,
    name: '',
    value: '',
    placeholder: null,
    maxLength: null,
    rows: 4,
    onChange: (name, value) => {},
};
TaskFormFieldTypeHtml.displayName = 'FieldType: Html';

//completed:
const TaskFormFieldTypeTextBox = React.memo(function TaskFormFieldTypeTextBox(props){
    const {
        value,
    } = props;

    //useRenderDebug('TaskFormFieldTypeTextBox', {props});

    if(!value) return null;

    return (
        <div dangerouslySetInnerHTML={{__html: value}}/>
    );
});
TaskFormFieldTypeTextBox.propTypes = {
    value: PropTypes.string,
};
TaskFormFieldTypeTextBox.defaultProps = {
    value: null,
};
TaskFormFieldTypeTextBox.displayName = 'FieldType: Text Box';

//completed:
const TaskFormFieldTypeSelect = React.memo(function TaskFormFieldTypeSelect(props){
    const {
        renderAs,
        url,
        store,
        required,
        name,
        value,
        idPath,
        displayPath,
        groupPath,
        missingGroupingValue,
        enableCollapsibleGroups,
        placeholder,
        emptyListText,
        columns,
        autoFlexColumns,
        isMulti,
        multiMin,
        multiMax,
        onChange,
    } = props;

    const [isLoading, options] = useAjaxLoader(url, idPath, displayPath, groupPath, missingGroupingValue);

    let FieldClass = SelectField;
    if(renderAs === 'multiselect') FieldClass = MultiSelectField;
    if(renderAs === 'radiogroup') FieldClass = RadioGroupField;
    if(renderAs === 'checkboxgroup') FieldClass = CheckboxGroupField;

    let parsedValue = value;
    if(_.includes(['multiselect', 'checkboxgroup'], renderAs)){
        parsedValue = _.filter(parsedValue, val => {return val !== null});
    }

    //useRenderDebug('TaskFormFieldTypeSelect', {props});

    if(store){
        let parsedStore = groupOptions(store, idPath, displayPath, groupPath, missingGroupingValue);

        return (
            <FieldClass
                name={name}
                required={required}
                value={parsedValue}
                placeholder={placeholder}
                emptyListText={emptyListText}
                columns={columns}
                autoFlex={autoFlexColumns}
                multiMin={isMulti && _.isNumber(multiMin) ? multiMin : null}
                multiMax={isMulti && _.isNumber(multiMax) ? multiMax : null}
                enableCollapsibleGroups={enableCollapsibleGroups}
                options={parsedStore}
                onChange={onChange}
            />
        );
    }

    return (
        <FieldClass
            name={name}
            required={required}
            value={parsedValue}
            placeholder={placeholder}
            emptyListText={emptyListText}
            columns={columns}
            autoFlex={autoFlexColumns}
            multiMin={isMulti && _.isNumber(multiMin) ? multiMin : null}
            multiMax={isMulti && _.isNumber(multiMax) ? multiMax : null}
            onChange={onChange}
            enableCollapsibleGroups={enableCollapsibleGroups}
            options={options}
            isLoading={isLoading}
        />
    );
});
TaskFormFieldTypeSelect.propTypes = {
    renderAs: PropTypes.oneOf(['select', 'combo', 'multiselect', 'radiogroup', 'checkboxgroup']),
    url: PropTypes.string,
    store: PropTypes.array,
    required: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.any,
    idPath: PropTypes.string.isRequired,
    displayPath: PropTypes.string.isRequired,
    groupPath: PropTypes.string,
    missingGroupingValue: PropTypes.string,
    enableCollapsibleGroups: PropTypes.bool,
    placeholder: PropTypes.string,
    emptyListText: PropTypes.string,
    columns: PropTypes.number,
    autoFlexColumns: PropTypes.bool,
    isMulti: PropTypes.bool,
    multiMin: PropTypes.number,
    multiMax: PropTypes.number,
    onChange: PropTypes.func,
};
TaskFormFieldTypeSelect.defaultProps = {
    renderAs: 'select',
    url: null,
    store: null,
    required: false,
    name: '',
    value: '',
    idPath: 'id',
    displayPath: null,
    groupPath: null,
    missingGroupingValue: 'Other',
    enableCollapsibleGroups: false,
    emptyListText: 'No Records',
    placeholder: null,
    columns: null,
    autoFlexColumns: true,
    isMulti: false,
    multiMin: null,
    multiMax: null,
    onChange: (name, value) => {},
};
TaskFormFieldTypeSelect.displayName = 'FieldType: Select';

//completed:
const TaskFormFieldTypeDatagroup = React.memo(function TaskFormFieldTypeDatagroup(props){
    const {
        renderAs,
        required,
        value,
        name,
        idPath,
        displayPath,
        groupPath,
        placeholder,
        emptyListText,
        datagroupAlias,
        datagroupParentAlias,
        parentFilterBy,
        orderBy,
        orderDirection,
        columns,
        autoFlexColumns,
        isMulti,
        multiMin,
        multiMax,
        indexFromParent,
        onChange,
    } = props;

    const [url, setUrl] = React.useState(null);
    const [parentDatagroup, setParentDatagroup] = React.useState(null);
    const [parentDatagroupValue, setParentDatagroupValue] = React.useState(null);
    const [isLoading, options] = useAjaxLoader(url, idPath, displayPath, groupPath);

    const onParentChange = React.useCallback(event => {
        let value = Boolean(event.target.dataset.value) ? event.target.dataset.value : null;
        setParentDatagroupValue(value);
    }, [onChange]);

    React.useEffect(() => {
        let url = [`/p/Datagroups/admin/DatagroupValues/getAll.json?datagroup_alias=${datagroupAlias}&deleted=0&normalize=1`];
        if(orderBy){
            let order = JSON.stringify([{property: orderBy, direction: orderDirection}]);
            url.push(`sort=${order}`);
        }
        if(parentFilterBy){
            let filter = JSON.stringify([{property: parentFilterBy, value: _.split(parentDatagroupValue, ',')}]);
            url.push(`filter=${filter}`);
        }
        if(parentFilterBy && !Boolean(parentDatagroupValue)) {
            url = null;
        }
        if(url) url = _.join(url, '&');

        setUrl(url);
    }, [datagroupAlias, orderBy, orderDirection, parentFilterBy, parentDatagroup, parentDatagroupValue]);

    React.useEffect(() => {
        if(datagroupParentAlias){
            let el = document.querySelector(`*[data-datagroup-alias="${datagroupParentAlias}-${indexFromParent}"]`);
            setParentDatagroup(el);
            if(el){
                el.addEventListener('parentchange', onParentChange);
                return () => {
                    el.removeEventListener('parentchange', onParentChange);
                }
            }
        }
    }, [datagroupParentAlias]);

    let FieldClass = SelectField;
    if(renderAs === 'multiselect') FieldClass = MultiSelectField;
    if(renderAs === 'radiogroup') FieldClass = RadioGroupField;
    if(renderAs === 'checkboxgroup') FieldClass = CheckboxGroupField;

    let parsedValue = value;
    if(_.includes(['multiselect', 'checkboxgroup'], renderAs)){
        parsedValue = _.filter(parsedValue, val => {return val !== null});
    }

    // useRenderDebug(`TaskFormFieldTypeDatagroup|${name}`, {props, url, parentDatagroup, parentDatagroupValue, isLoading, options});

    return (
        <FieldClass
            name={indexFromParent ? `${name}-${indexFromParent}` : name}
            required={required}
            value={parsedValue}
            placeholder={placeholder}
            emptyListText={emptyListText}
            columns={columns}
            autoFlex={autoFlexColumns}
            multiMin={isMulti && _.isNumber(multiMin) ? multiMin : null}
            multiMax={isMulti && _.isNumber(multiMax) ? multiMax : null}
            datagroupAlias={`${datagroupAlias}-${indexFromParent}`}
            onChange={onChange}
            options={options}
            isLoading={isLoading}
        />
    );
});
TaskFormFieldTypeDatagroup.propTypes = {
    renderAs: PropTypes.oneOf(['combo', 'multiselect', 'radiogroup', 'checkboxgroup']),
    required: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.any,
    idPath: PropTypes.string.isRequired,
    displayPath: PropTypes.string.isRequired,
    groupPath: PropTypes.string,
    placeholder: PropTypes.string,
    emptyListText: PropTypes.string,
    datagroupAlias: PropTypes.string.isRequired,
    datagroupParentAlias: PropTypes.string,
    parentFilterBy: PropTypes.string,
    orderBy: PropTypes.string,
    orderDirection: PropTypes.oneOf(['asc', 'desc']),
    columns: PropTypes.number,
    autoFlexColumns: PropTypes.bool,
    isMulti: PropTypes.bool,
    multiMin: PropTypes.number,
    multiMax: PropTypes.number,
    indexFromParent: PropTypes.number,
    onChange: PropTypes.func,
};
TaskFormFieldTypeDatagroup.defaultProps = {
    renderAs: 'combo',
    required: false,
    name: '',
    value: '',
    idPath: 'id',
    displayPath: null,
    groupPath: null,
    emptyListText: 'No Records',
    datagroupAlias: null,
    datagroupParentAlias: null,
    parentFilterBy: null,
    orderBy: null,
    orderDirection: 'asc',
    placeholder: null,
    columns: null,
    autoFlexColumns: true,
    isMulti: false,
    multiMin: null,
    multiMax: null,
    indexFromParent: null,
    onChange: (name, value) => {},
};
TaskFormFieldTypeDatagroup.displayName = 'FieldType: Datagroup';

//completed:
const TaskFormFieldTypeEmail = React.memo(function TaskFormFieldTypeEmail(props){
    const {
        required,
        value,
        name,
        placeholder,
        onChange,
    } = props;

    //useRenderDebug('TaskFormFieldTypeEmail', {props});

    return (
        <TextField
            type={'email'}
            required={required}
            name={name}
            value={value}
            placeholder={placeholder}
            onChange={onChange}
        />
    );
});
TaskFormFieldTypeEmail.propTypes = {
    required: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.string,
    placeholder: PropTypes.string,
    onChange: PropTypes.func,
};
TaskFormFieldTypeEmail.defaultProps = {
    required: false,
    name: '',
    value: '',
    placeholder: null,
    onChange: (name, value) => {},
};
TaskFormFieldTypeEmail.displayName = 'FieldType: Email';

//completed:
const TaskFormFieldTypeBirthday = React.memo(function TaskFormFieldTypeBirthday(props){
    const {
        required,
        name,
        value,
        placeholder,
        minValue,
        maxValue,
        onChange,
    } = props;

    //useRenderDebug('TaskFormFieldTypeBirthday', {props});

    return (
        <DateField
            required={required}
            name={name}
            placeholder={placeholder}
            value={value}
            minValue={minValue}
            maxValue={maxValue}
            onChange={onChange}
        />
    );
});
TaskFormFieldTypeBirthday.propTypes = {
    required: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.string,
    placeholder: PropTypes.string,
    minValue: PropTypes.string,
    maxValue: PropTypes.string,
    onChange: PropTypes.func,
};
TaskFormFieldTypeBirthday.defaultProps = {
    required: false,
    name: '',
    value: '',
    placeholder: null,
    minValue: null,
    maxValue: null,
    onChange: (name, value) => {},
};
TaskFormFieldTypeBirthday.displayName = 'FieldType: Birthday';

//completed:
const TaskFormFieldTypeGender = React.memo(function TaskFormFieldTypeGender(props){
    const {
        required,
        value,
        name,
        placeholder,
        onChange,
    } = props;

    let options = React.useRef([{
        group: "_ungrouped_",
        options: [{
            value: 'm',
            displayValue: 'Male',
        }, {
            value: 'f',
            displayValue: 'Female',
        }]
    }]);

    //useRenderDebug('TaskFormFieldTypeGender', {props});

    return (
        <SelectField
            required={required}
            name={name}
            value={value}
            placeholder={placeholder}
            options={options.current}
            onChange={onChange}
        />
    );
});
TaskFormFieldTypeGender.propTypes = {
    required: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.string,
    placeholder: PropTypes.string,
    onChange: PropTypes.func,
};
TaskFormFieldTypeGender.defaultProps = {
    required: false,
    name: '',
    value: '',
    placeholder: null,
    onChange: (name, value) => {},
};
TaskFormFieldTypeGender.displayName = 'FieldType: Gender';

//completed:
const TaskFormFieldTypeEmailType = React.memo(function TaskFormFieldTypePhoneType(props){
    const {
        required,
        value,
        name,
        placeholder,
        onChange,
    } = props;

    const [isLoading, options] = useAjaxLoader('/p/People/admin/PersonEmailTypes/getAll.json', 'id', 'label');

    //useRenderDebug('TaskFormFieldTypePhoneType', {props});

    return (
        <SelectField
            required={required}
            name={name}
            value={value}
            placeholder={placeholder}
            emptyListText={'No email types available'}
            onChange={onChange}
            options={options}
            isLoading={isLoading}
        />
    );
});
TaskFormFieldTypeEmailType.propTypes = {
    required: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.any,
    placeholder: PropTypes.string,
    onChange: PropTypes.func,
};
TaskFormFieldTypeEmailType.defaultProps = {
    required: false,
    name: '',
    value: '',
    placeholder: null,
    onChange: (name, value) => {},
};
TaskFormFieldTypeEmailType.displayName = 'FieldType: Email Type';

//completed:
const TaskFormFieldTypeAddressCountry = React.memo(function TaskFormFieldTypeAddressCountry(props){
    const {
        required,
        value,
        name,
        placeholder,
        onChange,
    } = props;

    const [isLoading, options] = useAjaxLoader('/p/Locations/admin/LocationCountries/getAll.json?sort=name', 'id', 'name');

    //useRenderDebug('TaskFormFieldTypeAddressCountry', {props, isLoading, options});

    return (
        <SelectField
            required={required}
            name={name}
            value={value}
            placeholder={placeholder}
            emptyListText={'No countries available'}
            onChange={onChange}
            options={options}
            isLoading={isLoading}
        />
    );
});
TaskFormFieldTypeAddressCountry.propTypes = {
    required: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.any,
    placeholder: PropTypes.string,
    onChange: PropTypes.func,
};
TaskFormFieldTypeAddressCountry.defaultProps = {
    required: false,
    name: '',
    value: '',
    placeholder: null,
    onChange: (name, value) => {},
};
TaskFormFieldTypeAddressCountry.displayName = 'FieldType: Address Country';

//completed:
const TaskFormFieldTypeAddressState = React.memo(function TaskFormFieldTypeAddressState(props){
    const {
        countryId,
        required,
        value,
        name,
        placeholder,
        onChange,
    } = props;

    const previousCountryId = React.useRef(countryId);
    const [isLoading, options] = useAjaxLoader(countryId ? `/p/Locations/admin/LocationStates/getAll.json?sort=name&location_country_id=${countryId}` : null, 'id', 'name');

    React.useEffect(() => {
        if (!countryId) {
            onChange(name, previousCountryId.current !== null && previousCountryId.current !== countryId ? null : value);
        }
        if (countryId !== previousCountryId.current) {
            previousCountryId.current = countryId;
        }
    }, [countryId]);

    //useRenderDebug('TaskFormFieldTypeAddressState', {props});

    return (
        <SelectField
            required={required}
            name={name}
            value={value}
            placeholder={placeholder}
            emptyListText={countryId ? 'No states available for selected country' : 'Select a country to load states'}
            onChange={onChange}
            options={options}
            isLoading={isLoading}
        />
    );
});
TaskFormFieldTypeAddressState.propTypes = {
    countryId: PropTypes.any,
    required: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.any,
    placeholder: PropTypes.string,
    onChange: PropTypes.func,
};
TaskFormFieldTypeAddressState.defaultProps = {
    countryId: null,
    required: false,
    name: '',
    value: '',
    placeholder: null,
    onChange: (name, value) => {},
};
TaskFormFieldTypeAddressState.displayName = 'FieldType: Address State';

//completed:
const TaskFormFieldTypeAddressType = React.memo(function TaskFormFieldTypeAddressType(props){
    const {
        required,
        value,
        name,
        placeholder,
        onChange,
    } = props;

    const [isLoading, options] = useAjaxLoader('/p/People/admin/PersonAddressTypes/getAll.json', 'id', 'label');

    //useRenderDebug('TaskFormFieldTypeAddressType', {props});

    return (
        <SelectField
            required={required}
            name={name}
            value={value}
            placeholder={placeholder}
            emptyListText={'No address types available'}
            onChange={onChange}
            options={options}
            isLoading={isLoading}
        />
    );
});
TaskFormFieldTypeAddressType.propTypes = {
    required: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.any,
    placeholder: PropTypes.string,
    onChange: PropTypes.func,
};
TaskFormFieldTypeAddressType.defaultProps = {
    required: false,
    name: '',
    value: '',
    placeholder: null,
    onChange: (name, value) => {},
};
TaskFormFieldTypeAddressType.displayName = 'FieldType: Address Type';

//completed:
const TaskFormFieldTypePhoneDefault = React.memo(function TaskFormFieldTypePhoneDefault(props){
    const {
        required,
        value,
        name,
        placeholder,
        onChange,
    } = props;

    const change = React.useCallback((n, v) => {
        onChange(name, v === '1');
    }, [onChange, name]);

    const options = React.useRef([{
        group: "_ungrouped_",
        options: [{
            value: '1',
            displayValue: 'Yes',
        }, {
            value: '0',
            displayValue: 'No',
        }]
    }]);

    //useRenderDebug('TaskFormFieldTypePhoneDefault', {props});

    return (
        <SelectField
            required={required}
            name={name}
            value={value === true ? '1' : value === false ? '0' : ''}
            placeholder={placeholder}
            showEmptyOption={false}
            options={options.current}
            onChange={change}
        />
    );
});
TaskFormFieldTypePhoneDefault.propTypes = {
    required: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.any,
    placeholder: PropTypes.string,
    onChange: PropTypes.func,
};
TaskFormFieldTypePhoneDefault.defaultProps = {
    required: false,
    name: '',
    value: '',
    placeholder: null,
    onChange: (name, value) => {},
};
TaskFormFieldTypePhoneDefault.displayName = 'FieldType: Phone Default';

//completed:
const TaskFormFieldTypePhoneType = React.memo(function TaskFormFieldTypePhoneType(props){
    const {
        required,
        value,
        name,
        placeholder,
        onChange,
    } = props;

    const [isLoading, options] = useAjaxLoader('/p/People/admin/PersonPhoneTypes/getAll.json', 'id', 'label');

    //useRenderDebug('TaskFormFieldTypePhoneType', {props});

    return (
        <SelectField
            required={required}
            name={name}
            value={value}
            placeholder={placeholder}
            emptyListText={'No phone types available'}
            onChange={onChange}
            options={options}
            isLoading={isLoading}
        />
    );
});
TaskFormFieldTypePhoneType.propTypes = {
    required: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.any,
    placeholder: PropTypes.string,
    onChange: PropTypes.func,
};
TaskFormFieldTypePhoneType.defaultProps = {
    required: false,
    name: '',
    value: '',
    placeholder: null,
    onChange: (name, value) => {},
};
TaskFormFieldTypePhoneType.displayName = 'FieldType: Phone Type';

//completed:
const TaskFormFieldTypeInstantMessengerType = React.memo(function TaskFormFieldTypeInstantMessengerType(props){
    const {
        required,
        value,
        name,
        placeholder,
        onChange,
    } = props;

    const [isLoading, options] = useAjaxLoader('/p/People/admin/PersonInstantMessengerTypes/getAll.json', 'id', 'label');

    //useRenderDebug('TaskFormFieldTypeInstantMessengerType', {props});

    return (
        <SelectField
            required={required}
            name={name}
            value={value}
            placeholder={placeholder}
            emptyListText={'No instant messenger types available'}
            onChange={onChange}
            options={options}
            isLoading={isLoading}
        />
    );
});
TaskFormFieldTypeInstantMessengerType.propTypes = {
    required: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.string,
    placeholder: PropTypes.string,
    onChange: PropTypes.func,
};
TaskFormFieldTypeInstantMessengerType.defaultProps = {
    required: false,
    name: '',
    value: '',
    placeholder: null,
    onChange: (name, value) => {},
};
TaskFormFieldTypeInstantMessengerType.displayName = 'FieldType: Instant Messenger Type';

//completed:
const TaskFormFieldTypeSocialNetworkType = React.memo(function TaskFormFieldTypeSocialNetworkType(props){
    const {
        required,
        value,
        name,
        placeholder,
        onChange,
    } = props;

    const [isLoading, options] = useAjaxLoader('/p/People/admin/PersonSocialMediaTypes/getAll.json', 'id', 'label');

    //useRenderDebug('TaskFormFieldTypeSocialNetworkType', {props});

    return (
        <SelectField
            required={required}
            name={name}
            value={value}
            placeholder={placeholder}
            emptyListText={'No social media types available'}
            onChange={onChange}
            options={options}
            isLoading={isLoading}
        />
    );
});
TaskFormFieldTypeSocialNetworkType.propTypes = {
    required: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.string,
    placeholder: PropTypes.string,
    onChange: PropTypes.func,
};
TaskFormFieldTypeSocialNetworkType.defaultProps = {
    required: false,
    name: '',
    value: '',
    placeholder: null,
    onChange: (name, value) => {},
};
TaskFormFieldTypeSocialNetworkType.displayName = 'FieldType: Social Network Type';

//completed:
const TaskFormFieldTypeHouseholdMember = React.memo(function TaskFormFieldTypeHouseholdMember(props){
    const {
        required,
        value,
        name,
        placeholder,
        onChange,
    } = props;

    const [isLoading, options] = useAjaxLoader('/p/People/People/getHouseholdMembers.json', 'id', 'fullname');

    //useRenderDebug('TaskFormFieldTypeSocialNetworkType', {props});

    return (
        <SelectField
            required={required}
            name={name}
            value={value}
            placeholder={placeholder}
            emptyListText={'No members in your household'}
            onChange={onChange}
            options={options}
            isLoading={isLoading}
        />
    );
});
TaskFormFieldTypeHouseholdMember.propTypes = {
    required: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.string,
    placeholder: PropTypes.string,
    onChange: PropTypes.func,
};
TaskFormFieldTypeHouseholdMember.defaultProps = {
    required: false,
    name: '',
    value: '',
    placeholder: null,
    onChange: (name, value) => {},
};
TaskFormFieldTypeHouseholdMember.displayName = 'FieldType: Household Member';